import { FilterOperators } from '@kirin/api-client/contracts/table';
import type { FilterValue } from '@kirin/api-client/contracts/table';
import type {
  AlertVisibility,
  GroupedAlert,
} from '@kirin/api-client/contracts/alerts';
import {
  ORGANIZATION_ALERTS_FILTERABLE_FIELDS,
  ALERT_OCCURRENCES_FILTERABLE_FIELDS,
  type OrganizationAlertsFilterableField,
} from '@kirin/api-client/contracts/alerts';
// `organization-dashboard` is not an exposed package subpath — it is only
// re-exported as the `organizationDashboard` namespace from `contracts` (matches
// how TopAlerts.tsx consumes this type). Importing the subpath directly builds
// under jest's moduleNameMapper but fails `tsc` against the package exports map.
import { type organizationDashboard } from '@kirin/api-client/contracts';
import { decodeTitleFilterKey } from '@kirin/api-client/helpers/alerts';
import { DATA_TABLE_PERSISTENCE_KEYS } from '@/components/DataTable/types';
import { ALERTS_VISIBILITY_URL_PARAM } from '@/lib/alertVisibilityStorage';

type TooltipLabelItem =
  | string
  | {
      value: string;
      label?: string;
      metadata?: Record<string, unknown>;
    };

export const formatUserTooltipLabel = (item: TooltipLabelItem): string => {
  if (typeof item === 'string') {
    return item;
  }

  const metadata = item.metadata as
    | {
        name?: string | null;
      }
    | undefined;

  return metadata?.name || item.label || item.value;
};

/**
 * Canonical URL key mapping for all organization alerts filterable fields.
 * This is the single source of truth for filter URL keys — column definitions
 * and deep link builders must reference this constant instead of hardcoding strings.
 *
 * The `satisfies` constraint enforces exhaustiveness: TypeScript will error if a
 * new field is added to ORGANIZATION_ALERTS_FILTERABLE_FIELDS without a URL key here.
 */
export const ALERTS_FILTER_URL_KEYS = {
  [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.TYPE]: 't',
  // KIR-3769: distinct key from `t` (type) so pre-existing type-based deep
  // links keep resolving to the Type filter and never collide with the new
  // composite Title filter.
  [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.TITLE]: 'ti',
  [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.SEVERITY]: 'sev',
  [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.OUTCOME]: 'out',
  [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.FILE_PATH]: 'fp',
  [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.USERS]: 'u',
  [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.LAST_SEEN_AT]: 'ls',
  [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.FIRST_SEEN_AT]: 'fs',
  [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.DESCRIPTION]: 'desc',
  [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.LOCATION]: 'loc',
  [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.OCCURRENCE_COUNT]: 'oc',
  [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.SCORE]: 'sc',
  [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.CLIENT_TYPES]: 'ct',
  [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.ENDPOINTS]: 'ep',
  [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.SECURITY_GROUPS]: 'sg',
  // KIR-5075: projects-inventory project id (Risk Map deep links).
  [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.PROJECTS]: 'pr',
  // KIR-4440: feedback status verdict + comment text search.
  [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.STATUS]: 'stat',
  [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.COMMENT]: 'cm',
  // KIR-5537: Last Seen on the filtered endpoint (endpoint page links).
  [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.ENDPOINT_LAST_SEEN_AT]: 'els',
} as const satisfies Record<OrganizationAlertsFilterableField, string>;

/**
 * Configuration for the organization alerts DataTable.
 * Used for building deep link URLs and state persistence.
 *
 * ⚠️ KIR-2659 deep-link contract: `tableId: 'org-alt'` is ALSO baked
 * into URLs constructed outside React by the desktop agent and the
 * IDE extension when they build summary-banner "View Details" deep
 * links — see:
 *   - extensions/agent/pkg/notifications/coordinator.go
 *     (buildSummaryDeepLinkURL)
 *   - extensions/vscode-core/src/services/notifications/coordinator.ts
 *     (buildSummaryDeepLinkURL)
 * Both emit URLs shaped like
 *   /<orgId>/alerts?org-alt.sev[includes]=high&org-alt.t[includes]=...
 * Renaming this id silently breaks every notification deep-link to
 * the org alerts view. Update the agent + extension URL builders in
 * the same PR if you change it. Contract tests exercise the encoding
 * in coordinator_test.go and __tests__/coordinator.test.ts.
 */
export const ALERTS_TABLE_CONFIG = {
  /** DataTable id - must match the id prop on the DataTable component */
  tableId: 'org-alt',
  /** Default filter operator when not specified */
  defaultOperator: FilterOperators.INCLUDES,
} as const;

/**
 * Filter definition for building deep link URLs.
 */
export interface AlertsDeepLinkFilter {
  field: OrganizationAlertsFilterableField;
  operator: string;
  values: string[];
}

/**
 * Creates a filter definition with the default operator.
 */
export function createFilter(
  field: OrganizationAlertsFilterableField,
  values: string[],
  operator: string = ALERTS_TABLE_CONFIG.defaultOperator
): AlertsDeepLinkFilter {
  return { field, operator, values };
}

/**
 * Builds a deep link URL to the alerts page with pre-applied filters.
 *
 * @param options.expandedRowId - row to auto-expand on landing.
 * @param options.visibility - seeds the Active / All / Suppressed tab,
 *   overriding the stored preference for this navigation only. Stripped
 *   by `useAlertVisibility` after first read.
 */
export function buildAlertsDeepLinkUrl(
  organizationId: string | undefined,
  filters: AlertsDeepLinkFilter[],
  options?: { expandedRowId?: string; visibility?: AlertVisibility }
): string | null {
  if (!organizationId) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        'buildAlertsDeepLinkUrl: organizationId is required to build deep link URL'
      );
    }
    return null;
  }

  const basePath = `/${organizationId}/alerts`;
  const params = new URLSearchParams();
  const { tableId } = ALERTS_TABLE_CONFIG;

  for (const filter of filters) {
    if (filter.values.length === 0) continue;

    const urlKey = ALERTS_FILTER_URL_KEYS[filter.field];
    const paramKey = `${tableId}.${urlKey}[${filter.operator}]`;
    // Encode each value before joining so commas inside values don't
    // collide with the comma separator that decodeFilterValues splits on.
    const paramValue = filter.values.map(v => encodeURIComponent(v)).join(',');

    params.set(paramKey, paramValue);
  }

  // Add expanded row ID if provided
  if (options?.expandedRowId) {
    const expandedKey = `${tableId}.${DATA_TABLE_PERSISTENCE_KEYS.url.expanded}`;
    // URLSearchParams handles encoding
    params.set(expandedKey, options.expandedRowId);
  }

  if (options?.visibility) {
    params.set(ALERTS_VISIBILITY_URL_PARAM, options.visibility);
  }

  const queryString = params.toString();
  return queryString ? `${basePath}?${queryString}` : basePath;
}

/**
 * Builds a deep link URL for a GroupedAlert (from LatestAlertsWidget).
 * Filters by type, severity, and filePath (if available) to scope the alerts list,
 * then expands the specific alert row via alertKey.
 *
 * Description is intentionally excluded — it has no column definition in the alerts
 * table, so it cannot be decoded from the URL on hydration.
 *
 * @param organizationId - The organization ID
 * @param alert - The grouped alert data
 * @returns Deep link URL to alerts page filtered to this alert with row expanded, or null if organizationId is missing
 */
export function buildGroupedAlertDeepLinkUrl(
  organizationId: string | undefined,
  alert: Pick<GroupedAlert, 'type' | 'severity' | 'filePath' | 'alertKey'>
): string | null {
  const filters: AlertsDeepLinkFilter[] = [
    createFilter(ORGANIZATION_ALERTS_FILTERABLE_FIELDS.TYPE, [alert.type]),
    createFilter(ORGANIZATION_ALERTS_FILTERABLE_FIELDS.SEVERITY, [
      alert.severity,
    ]),
  ];

  if (alert.filePath) {
    filters.push(
      createFilter(ORGANIZATION_ALERTS_FILTERABLE_FIELDS.FILE_PATH, [
        alert.filePath,
      ])
    );
  }

  return buildAlertsDeepLinkUrl(organizationId, filters, {
    expandedRowId: alert.alertKey,
  });
}

/**
 * Builds a deep link URL for a Top Alerts dashboard widget row (KIR-3809).
 *
 * `alertName` is the composite `<subPolicyKey>::<type>` title key, the same
 * identity the Alerts page groups on — so the link targets the **visible**
 * Title column (revamp on) or Type column (revamp off). Both render a header
 * with a filter icon the user can see and clear; a hidden column would render
 * neither, which was the original bug.
 *
 * No row is expanded: a title maps to many grouped alerts, and the widget row
 * carries no `alertKey`.
 *
 * Severity is applied alongside the title because the row shows a severity
 * badge and the ticket asks for "that alert's type/severity". Note `riskLevel`
 * is a max() over the title group while `issuesCount` spans all severities, so
 * the landed table is the max-severity subset of the row — intentional, it
 * matches the badge the user clicked.
 *
 * @param organizationId - The organization ID
 * @param alert - The top-alert row (composite `alertName` + `riskLevel`)
 * @param revampEnabled - ALERTS_REVAMP flag; picks the Title or Type filter
 * @returns Deep link URL to the filtered alerts page, or null if organizationId is missing
 */
export function buildTopAlertDeepLinkUrl(
  organizationId: string | undefined,
  alert: Pick<
    organizationDashboard.OrganizationDashboardTopAlert,
    'alertName' | 'riskLevel'
  >,
  revampEnabled: boolean
): string | null {
  const titleFilter = revampEnabled
    ? createFilter(ORGANIZATION_ALERTS_FILTERABLE_FIELDS.TITLE, [
        alert.alertName,
      ])
    : createFilter(ORGANIZATION_ALERTS_FILTERABLE_FIELDS.TYPE, [
        decodeTitleFilterKey(alert.alertName).type,
      ]);

  return buildAlertsDeepLinkUrl(
    organizationId,
    [
      titleFilter,
      createFilter(ORGANIZATION_ALERTS_FILTERABLE_FIELDS.SEVERITY, [
        alert.riskLevel,
      ]),
    ],
    // Pin the only tab guaranteed to contain the clicked row. The widget
    // counts muted findings by deliberate product policy
    // (`getOrganizationDashboardTopAlerts`), while the Active tab hides them
    // — so a title whose findings are all muted shows a real issuesCount in
    // the widget and an empty table on landing.
    // "All" can only ever show more than the row promised, never zero. (It is
    // a superset, not an exact match: the widget drops excluded findings that
    // "All" keeps. The exact population — muted in,
    // excluded out — is not expressible as one `visibility` value, and erring
    // wide beats landing empty.) Seeds the tab for this navigation only —
    // `useAlertVisibility` strips the param and never writes it to storage.
    { visibility: 'all' }
  );
}

/**
 * Mapping from main alerts table filterable fields to their equivalent
 * occurrences filterable fields. Only fields applicable to individual
 * occurrence rows are included.
 *
 * Both LAST_SEEN_AT and FIRST_SEEN_AT map to the occurrences `lastSeenAt`
 * (per-row `scannedAt`) because each occurrence has a single timestamp —
 * the grouped alert's "first/last seen" distinction doesn't apply at the
 * row level. The previous exclusion of date filters was removed because the
 * requirement is that date constraints from the main table carry through to
 * the modal: "older variations may not appear when filtered" is intentional.
 *
 * When both first and last seen filters are active simultaneously, we keep
 * only the lastSeenAt-sourced filter (direct semantic match) to avoid
 * sending duplicate `lastSeenAt` filters to the backend.
 */
const MAIN_TO_OCCURRENCES_FIELD_MAP: Partial<
  Record<OrganizationAlertsFilterableField, string>
> = {
  [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.USERS]:
    ALERT_OCCURRENCES_FILTERABLE_FIELDS.USERS,
  [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.LAST_SEEN_AT]:
    ALERT_OCCURRENCES_FILTERABLE_FIELDS.LAST_SEEN_AT,
  [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.FIRST_SEEN_AT]:
    ALERT_OCCURRENCES_FILTERABLE_FIELDS.LAST_SEEN_AT,
  [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.FILE_PATH]:
    ALERT_OCCURRENCES_FILTERABLE_FIELDS.FILE_PATH,
  [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.CLIENT_TYPES]:
    ALERT_OCCURRENCES_FILTERABLE_FIELDS.CLIENT_TYPES,
  // KIR-5205: Score is not carried: the list filters the overridden score,
  // occurrences keep the recorded one.
  // KIR-5098: only occurrences on a matching endpoint.
  [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.ENDPOINTS]:
    ALERT_OCCURRENCES_FILTERABLE_FIELDS.ENDPOINTS,
  // KIR-5537: with the endpoint filter carried, the same rows per occurrence.
  [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.ENDPOINT_LAST_SEEN_AT]:
    ALERT_OCCURRENCES_FILTERABLE_FIELDS.LAST_SEEN_AT,
};

/**
 * Extract filters applicable to the occurrences modal from the current URL
 * search params. Reads the DataTable's persisted filter state and maps only
 * the applicable fields (users, lastSeenAt, filePath, clientTypes, endpoints,
 * endpointLastSeenAt)
 * to the occurrences API filter shape.
 *
 * Filters that have no equivalent in the occurrences table (severity, score,
 * outcome, type, occurrenceCount, firstSeenAt, description, location) are
 * silently ignored.
 */
export function getOccurrencesFiltersFromSearchParams(
  searchParams: URLSearchParams
): FilterValue[] {
  const { tableId } = ALERTS_TABLE_CONFIG;
  const filters: { filter: FilterValue; sourceField: string }[] = [];

  // Build reverse map: urlKey → field name
  const urlKeyToField = Object.fromEntries(
    Object.entries(ALERTS_FILTER_URL_KEYS).map(([field, urlKey]) => [
      urlKey,
      field,
    ])
  );

  // URL pattern: {tableId}.{urlKey}[{operator}]={csv values}
  const prefix = `${tableId}.`;

  searchParams.forEach((value, key) => {
    if (!key.startsWith(prefix)) return;

    const rest = key.slice(prefix.length);
    const bracketMatch = rest.match(/^([^[]+)\[([^\]]+)\]$/);
    if (!bracketMatch) return;

    const urlKey = bracketMatch[1];
    const operator = bracketMatch[2];
    const mainField = urlKeyToField[urlKey];
    if (!mainField) return;

    // Check if this field maps to an occurrences-applicable field
    const occField =
      MAIN_TO_OCCURRENCES_FIELD_MAP[
        mainField as OrganizationAlertsFilterableField
      ];
    if (!occField) return;

    const values = value
      .split(',')
      .map(v => {
        try {
          return decodeURIComponent(v.trim());
        } catch {
          return v.trim();
        }
      })
      .filter(Boolean);

    if (values.length === 0) return;

    filters.push({
      filter: {
        field: occField,
        operator: operator as FilterValue['operator'],
        value: values,
      },
      sourceField: mainField,
    });
  });

  // Deduplicate: if both firstSeenAt and lastSeenAt mapped to the same
  // occurrences field (lastSeenAt), prefer the lastSeenAt-sourced entry
  // since it's the direct semantic match to per-row scannedAt.
  const byField = new Map<string, (typeof filters)[number]>();
  for (const entry of filters) {
    const existing = byField.get(entry.filter.field);
    if (!existing) {
      byField.set(entry.filter.field, entry);
    } else if (
      entry.sourceField === ORGANIZATION_ALERTS_FILTERABLE_FIELDS.LAST_SEEN_AT
    ) {
      // lastSeenAt wins over firstSeenAt
      byField.set(entry.filter.field, entry);
    }
  }

  return [...byField.values()].map(e => e.filter);
}
